import { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from "@material-ui/core";
import { Refresh, Publish, Undo, Lock } from "@material-ui/icons";
import { api } from "../../services/apiService";
import {
  pendingResult,
  publishOutcome,
  retractOutcome,
  groupSkipsByReason,
  summarizeSelection,
  SKIP_REASON_HELP,
  toggleId,
  selectableIds,
  isValidBatchId,
  isStemPlaced,
  isDangerFlagged,
  buildPublishRequest,
  overrideReasonMissing,
} from "./review-publish-logic";
import Menu from "../menu/menu";
import "./review-publish.scss";

const EXAM_TYPE = "ABSITE";
const REVIEW_TYPE = "IMAGE_CANDIDATE";
// The publish worklist is small by construction (only APPROVED+UNPUBLISHED
// items); one page covers the pilot pool with room to spare.
const PAGE_SIZE = 100;

// Same role-gated-proxy pattern as the reviewer queue: a plain <img src> can't
// carry the Bearer token, so fetch the bytes and render an object URL.
function PendingThumb({ itemId }) {
  const [src, setSrc] = useState(null);
  const [failed, setFailed] = useState(false);
  // Bumping retry re-runs the fetch — a transient failure isn't terminal
  // (same affordance as the reviewer queue's CandidateImage).
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    let objectUrl;
    let active = true;
    setFailed(false);
    setSrc(null);
    api
      .getReviewItemImage(itemId)
      .then((objUrl) => {
        if (!active) {
          URL.revokeObjectURL(objUrl);
          return;
        }
        objectUrl = objUrl;
        setSrc(objUrl);
      })
      .catch(() => active && setFailed(true));
    return () => {
      active = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [itemId, retry]);
  if (failed) {
    return (
      <button type="button" className="rp-thumb rp-thumb--failed" onClick={() => setRetry((r) => r + 1)}>
        image failed — retry
      </button>
    );
  }
  if (!src) return <div className="rp-thumb"><CircularProgress size={18} /></div>;
  return <img className="rp-thumb" src={src} alt="approved candidate" />;
}

// Shared outcome line for both retract entry points (one-click batch +
// manual id), so the message always appears next to the button clicked.
function RetractMessage({ retractMsg }) {
  if (!retractMsg) return null;
  return (
    <Typography
      variant="body2"
      className={retractMsg.tone === "ok" ? "rp-retract-ok" : "rp-retract-error"}
    >
      {retractMsg.text}
    </Typography>
  );
}

// The skip-reason table (audit P0-2: reasons existed server-side, invisible to
// a human). One row per reason with operator help + the affected item ids.
function SkipTable({ skipped }) {
  const rows = groupSkipsByReason(skipped);
  if (rows.length === 0) return null;
  return (
    <div className="rp-skips">
      <Typography variant="subtitle2">Skipped</Typography>
      {rows.map(({ reason, ids }) => (
        <div className="rp-skip-row" key={reason}>
          <Chip size="small" label={`${reason} × ${ids.length}`} className="rp-skip-chip" />
          <span className="rp-skip-help">{SKIP_REASON_HELP[reason] || SKIP_REASON_HELP.UNKNOWN}</span>
          <span className="rp-skip-ids">{ids.join(", ")}</span>
        </div>
      ))}
    </div>
  );
}

export default function ReviewPublishPage() {
  const role = window.localStorage.getItem("role");
  const canView = role === "ROLE_ADMIN"; // backend endpoints are @Secured(ROLE_ADMIN)

  const [pending, setPending] = useState([]);
  const [totalElements, setTotalElements] = useState(0);
  const [loading, setLoading] = useState(false);
  const [dark, setDark] = useState(false);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState([]);
  // S1.2/F-S5-1 (the P0-2 dead-end closer): PER-ITEM attest/override state — one audited
  // checkbox per affected row, never a batch-level boolean (OVERRIDE_BOOLEAN_RETIRED class).
  const [attested, setAttested] = useState([]);
  const [overrides, setOverrides] = useState([]);
  const [overrideReason, setOverrideReason] = useState("");
  const [publishing, setPublishing] = useState(false);
  // Last batch outcome — carries the batchId the one-click retract needs.
  const [lastBatch, setLastBatch] = useState(null);
  const [retracting, setRetracting] = useState(false);
  const [retractMsg, setRetractMsg] = useState(null);
  // Emergency path for a batch published outside this session (id from
  // publish logs / DB) — the console must not be blind to older batches.
  const [manualBatchId, setManualBatchId] = useState("");
  // E2: pre-action confirm gate. Publish pushes AI-generated medical images live
  // to students in one click; retract deletes live objects. Both now route through
  // a Dialog that states exactly what will happen (count + affected questions).
  //   null | { kind: "publish" } | { kind: "retract", batchId }
  const [confirm, setConfirm] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    api
      .getPublishPending({ type: REVIEW_TYPE, examType: EXAM_TYPE, page: 0, size: PAGE_SIZE })
      .then((res) => {
        const result = pendingResult(res);
        if (result.state === "dark") {
          setDark(true);
          setPending([]);
          return;
        }
        if (result.state === "error") {
          setError("Could not load the publish worklist.");
          setPending([]);
          return;
        }
        setDark(false);
        setPending(result.data.items);
        setTotalElements(result.data.totalElements || result.data.items.length);
        // Prune selections that no longer exist (published elsewhere / refreshed away).
        setSelected((sel) => sel.filter((id) => result.data.items.some((i) => i.id === id)));
        // Attest/override state follows the same pruning — a checked box must never
        // survive its row.
        setAttested((ids) => ids.filter((id) => result.data.items.some((i) => i.id === id)));
        setOverrides((ids) => ids.filter((id) => result.data.items.some((i) => i.id === id)));
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (canView) load();
  }, [canView, load]);

  const publishSelected = () => {
    setPublishing(true);
    setError(null);
    // F-S5-1: attested/override ids intersect the selection inside buildPublishRequest;
    // the no-extras path calls with itemIds alone (byte-identical to the pre-S1.2 request).
    const req = buildPublishRequest(selected, attested, overrides, overrideReason);
    (req.extras ? api.publishReviewItems(req.itemIds, req.extras) : api.publishReviewItems(req.itemIds))
      .then((res) => {
        const outcome = publishOutcome(res);
        if (outcome.state !== "ok") {
          setError(outcome.state === "dark"
            ? "Publishing is not enabled." : "Publish failed — nothing was flipped.");
          return;
        }
        setLastBatch(outcome);
        setRetractMsg(null);
        setSelected([]);
        // A consumed attestation/override never rides the next batch — each publish
        // re-examines and re-checks explicitly (audit honesty).
        setAttested([]);
        setOverrides([]);
        setOverrideReason("");
        load();
      })
      .finally(() => setPublishing(false));
  };

  const retract = (batchId) => {
    setRetracting(true);
    setRetractMsg(null);
    api
      .retractPublishBatch(batchId)
      .then((res) => {
        const outcome = retractOutcome(res);
        if (outcome.state === "dark") {
          // The rollback valve must never report a dark flag as a failure —
          // an incident responder needs the true state.
          setRetractMsg({ tone: "error", text: "The review queue is not enabled in this environment — nothing was retracted." });
          return;
        }
        if (outcome.state !== "ok") {
          setRetractMsg({ tone: "error", text: "Retract failed — the batch may still be live." });
          return;
        }
        setRetractMsg({
          tone: "ok",
          text: outcome.retracted.length === 0
            ? "Nothing to retract in that batch (already retracted, or wrong id)."
            : `Retracted ${outcome.retracted.length} item(s). Origin objects deleted; edge-cache invalidation runs when the CDN config is enabled.`,
        });
        load();
      })
      .finally(() => setRetracting(false));
  };

  // Fire the gated action, then close the dialog. The underlying publishing/
  // retracting state still drives the in-place button spinners.
  const runConfirm = () => {
    if (!confirm) return;
    if (confirm.kind === "publish") {
      publishSelected();
    } else if (confirm.kind === "retract") {
      retract(confirm.batchId);
    }
    setConfirm(null);
  };

  const publishSummary = confirm && confirm.kind === "publish"
    ? summarizeSelection(pending, selected)
    : null;

  // F-S5-1 fail-closed gate: a SELECTED override without a non-blank reason blocks publish
  // client-side (the server would 400 the same request — the reason is the audit row's text).
  const reasonMissing = overrideReasonMissing(selected, overrides, overrideReason);
  const anySelectedOverride = overrides.some((id) => selected.includes(id));

  if (!canView) {
    return (
      <Box className="body-cont-over rp-page-shell">
        <Menu />
        <div className="rp-page">
          <Paper className="rp-locked" elevation={0}>
            <Lock fontSize="small" /> Admin only.
          </Paper>
        </div>
      </Box>
    );
  }

  return (
    <Box className="body-cont-over rp-page-shell">
      <Menu />
      <div className="rp-page">
        <div className="rp-header">
          <Typography variant="h5">Publish Console</Typography>
          <Typography variant="body2" className="rp-subtitle">
            Approved {REVIEW_TYPE.toLowerCase().replace("_", " ")}s awaiting the explicit
            publish step (side-car CDN copy — never a bank write).
          </Typography>
          <Button startIcon={<Refresh />} onClick={load} disabled={loading}>Refresh</Button>
        </div>

        {dark && (
          <Paper className="rp-locked" elevation={0}>
            The review queue is not enabled in this environment.
          </Paper>
        )}
        {error && <Paper className="rp-error" elevation={0}>{error}</Paper>}
        {loading && <CircularProgress size={24} className="rp-spinner" />}

        {!dark && !loading && (
          <Paper className="rp-panel" elevation={0}>
            <div className="rp-panel-head">
              <Typography variant="subtitle1">
                Pending publish ({pending.length}{totalElements > pending.length ? ` of ${totalElements}` : ""})
              </Typography>
              <div className="rp-panel-actions">
                <Button
                  size="small"
                  onClick={() => setSelected(
                    selected.length === pending.length ? [] : selectableIds(pending))}
                  disabled={pending.length === 0}
                >
                  {selected.length === pending.length && pending.length > 0 ? "Clear all" : "Select all"}
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  startIcon={<Publish />}
                  onClick={() => setConfirm({ kind: "publish" })}
                  disabled={selected.length === 0 || publishing || reasonMissing}
                >
                  {publishing ? "Publishing…" : `Publish ${selected.length} selected`}
                </Button>
              </div>
            </div>
            {/* F-S5-1: the override justification — REQUIRED (non-blank) whenever a selected
                override is checked; recorded verbatim in the audit row. */}
            {anySelectedOverride && (
              <div className="rp-override-reason">
                <TextField
                  size="small"
                  variant="outlined"
                  fullWidth
                  multiline
                  required
                  label="Override reason"
                  placeholder="Why is it safe to publish despite the clinical-safety flag? (recorded in the audit row)"
                  value={overrideReason}
                  onChange={(e) => setOverrideReason(e.target.value)}
                  inputProps={{ "aria-label": "override reason" }}
                />
                {reasonMissing && (
                  <Typography variant="caption" className="rp-override-reason-hint">
                    A non-blank reason is required before publishing with an override.
                  </Typography>
                )}
              </div>
            )}
            {pending.length === 0 ? (
              <Typography variant="body2" className="rp-empty">
                Nothing approved is waiting to publish.
              </Typography>
            ) : (
              <div className="rp-rows">
                {pending.map((item) => (
                  <div className="rp-row" key={item.id}>
                    <Checkbox
                      checked={selected.includes(item.id)}
                      onChange={() => setSelected((sel) => toggleId(sel, item.id))}
                      inputProps={{ "aria-label": `select ${item.id}` }}
                    />
                    <PendingThumb itemId={item.id} />
                    <div className="rp-row-meta">
                      <div className="rp-row-q">Question {item.subjectKey}</div>
                      <div className="rp-row-sub">
                        {item.examType}
                        {item.visualKind ? ` · ${item.visualKind}` : ""}
                        {item.aggregate
                          ? ` · ${item.aggregate.approveCount}✓ / ${item.aggregate.rejectCount}✗`
                          : ""}
                      </div>
                      <div className="rp-row-id">{item.id}</div>
                      {/* F-S5-1: PER-ITEM audited affordances — each affected row carries its
                          OWN checkbox (batch-level booleans are banned). Unchecked = the
                          server's fail-closed skip (STEM_ATTESTATION_REQUIRED / DANGER_FLAGGED)
                          explains itself in the skip table. */}
                      {isStemPlaced(item) && (
                        <label className="rp-gate rp-gate--stem">
                          <Checkbox
                            size="small"
                            checked={attested.includes(item.id)}
                            onChange={() => setAttested((ids) => toggleId(ids, item.id))}
                            inputProps={{ "aria-label": `attest ${item.id}` }}
                          />
                          <span className="rp-gate-text">
                            I attest this stem-placed image does not reveal the answer
                            <span className="rp-gate-note"> (audited)</span>
                          </span>
                        </label>
                      )}
                      {isDangerFlagged(item) && (
                        <label className="rp-gate rp-gate--danger">
                          <Checkbox
                            size="small"
                            checked={overrides.includes(item.id)}
                            onChange={() => setOverrides((ids) => toggleId(ids, item.id))}
                            inputProps={{ "aria-label": `override ${item.id}` }}
                          />
                          <span className="rp-gate-text">
                            ⚑ Publish despite the clinical-safety flag
                            <span className="rp-gate-note"> (audited override — reason required)</span>
                          </span>
                        </label>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Paper>
        )}

        {lastBatch && (
          <Paper className="rp-panel" elevation={0}>
            <Typography variant="subtitle1">Last publish batch</Typography>
            <div className="rp-batch-line">
              <Chip size="small" label={`batch ${lastBatch.batchId}`} />
              <span>{lastBatch.published.length} published, {lastBatch.skipped.length} skipped</span>
              <Button
                size="small"
                startIcon={<Undo />}
                onClick={() => setConfirm({ kind: "retract", batchId: lastBatch.batchId })}
                disabled={retracting || lastBatch.published.length === 0}
              >
                Retract this batch
              </Button>
            </div>
            <SkipTable skipped={lastBatch.skipped} />
            <RetractMessage retractMsg={retractMsg} />
          </Paper>
        )}

        <Paper className="rp-panel" elevation={0}>
          <Typography variant="subtitle1">Retract a batch</Typography>
          <Typography variant="body2" className="rp-subtitle">
            Emergency rollback by publish-batch id (from a previous session's publish
            result or the publish logs). Deletes the public objects; the guarded flip
            marks items RETRACTED.
          </Typography>
          <div className="rp-retract-line">
            <TextField
              size="small"
              variant="outlined"
              placeholder="publish batch id (UUID)"
              value={manualBatchId}
              onChange={(e) => setManualBatchId(e.target.value)}
              className="rp-retract-input"
            />
            <Tooltip title={isValidBatchId(manualBatchId) ? "" : "Enter a valid UUID"}>
              <span>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<Undo />}
                  onClick={() => setConfirm({ kind: "retract", batchId: manualBatchId.trim() })}
                  disabled={!isValidBatchId(manualBatchId) || retracting}
                >
                  {retracting ? "Retracting…" : "Retract"}
                </Button>
              </span>
            </Tooltip>
          </div>
          <RetractMessage retractMsg={retractMsg} />
        </Paper>
      </div>

      {/* E2: the pre-action gate. Esc / backdrop / Cancel all dismiss it (MUI
          Dialog closes on Escape natively); the confirm button is autofocused so
          the keyboard flow is complete. Themed via tokens on the paper (no
          hardcoded surface) so it isn't a stark white card in dark mode. */}
      <Dialog
        open={Boolean(confirm)}
        onClose={() => setConfirm(null)}
        aria-labelledby="rp-confirm-title"
        PaperProps={{ className: "rp-confirm-paper" }}
      >
        {confirm && confirm.kind === "publish" && (
          <>
            <DialogTitle id="rp-confirm-title">
              Publish {publishSummary.count} image
              {publishSummary.count === 1 ? "" : "s"} to students?
            </DialogTitle>
            <DialogContent>
              <DialogContentText className="rp-confirm-text">
                These approved candidates go live to students immediately across{" "}
                {publishSummary.subjectKeys.length} question
                {publishSummary.subjectKeys.length === 1 ? "" : "s"}. This is a
                customer-visible action — retract is available afterward, but the
                images are seen the moment they publish.
              </DialogContentText>
              {publishSummary.subjectKeys.length > 0 && (
                <div className="rp-confirm-subjects">
                  {publishSummary.subjectKeys.map((k) => (
                    <Chip key={k} size="small" label={`Q ${k}`} className="rp-confirm-subject" />
                  ))}
                </div>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setConfirm(null)} className="rp-confirm-cancel">
                Cancel
              </Button>
              <Button
                variant="contained"
                color="primary"
                startIcon={<Publish />}
                onClick={runConfirm}
                autoFocus
              >
                Publish to students
              </Button>
            </DialogActions>
          </>
        )}
        {confirm && confirm.kind === "retract" && (
          <>
            <DialogTitle id="rp-confirm-title">Retract this batch from students?</DialogTitle>
            <DialogContent>
              <DialogContentText className="rp-confirm-text">
                Retracting removes the published image(s) from students and deletes
                the public objects for this batch. Items are marked RETRACTED and
                will not silently re-publish.
              </DialogContentText>
              <div className="rp-confirm-subjects">
                <Chip size="small" label={`batch ${confirm.batchId}`} className="rp-confirm-subject" />
              </div>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setConfirm(null)} className="rp-confirm-cancel">
                Cancel
              </Button>
              <Button
                variant="contained"
                color="secondary"
                startIcon={<Undo />}
                onClick={runConfirm}
                autoFocus
              >
                Retract
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
}
